// ABOUTME: Submits applicant stage changes and reconciles ambiguous responses.
// ABOUTME: Confirms persisted Mongo state before reporting an operation failure.

type ApiCall = (
    endpoint: string,
    verb: "post",
    params: Compleo.IObject
) => Promise<any>;

type ApplicantStageChangeResponse = {
    status: number;
    data?: Compleo.IObject;
};

type ApplicantStageCompletionErrorContext = {
    operation: "notification" | "synchronization" | "invalidation";
    queryKey?: string;
};

interface ExecuteApplicantStageChangeParams {
    submit: () => Promise<ApplicantStageChangeResponse>;
    rollback: () => void;
    onPersistenceError: (error: unknown) => void;
    onClose: () => void;
    synchronize: (response: ApplicantStageChangeResponse) => Promise<void>;
    listKeyToInvalidate: string[];
    invalidateQuery: (key: string) => Promise<unknown> | unknown;
    onPersisted: (
        response: ApplicantStageChangeResponse
    ) => Promise<unknown> | unknown;
    onCompletionError?: (
        error: unknown,
        context: ApplicantStageCompletionErrorContext
    ) => Promise<unknown> | unknown;
    successResponse: Compleo.IObject;
}

const emailAutomationWarningActions = new Set([
    "actionApplicantEmail",
    "actionApplicantSendEmailToUsers"
]);

const defaultApiCall: ApiCall = async (...args) => {
    const { apiDirectCall } = await import("customHooks/useApi");
    return apiDirectCall(...args);
};

const createRequestId = () => {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const getOperationResponse = (response: ApplicantStageChangeResponse) => {
    const operation = response.data?.operation || response.data || {};
    const canonicalStage = response.data?.canonicalStage;

    return {
        ...operation,
        canonicalStage,
        stageId: canonicalStage?.stageId || operation.stageId,
        warnings: operation.warnings || []
    };
};

const isTerminalOperation = (requestStatus?: string) =>
    ["completed", "manual_review", "superseded"].includes(requestStatus || "");

const isRecoverableOperation = (requestStatus?: string) =>
    ["accepted", "processing", "retryable"].includes(requestStatus || "");

const shouldReconcile = (error: any) => {
    const status = error?.response?.status;
    const errorCode =
        error?.response?.data?.errorCode ||
        error?.response?.data?.data?.errorCode;

    return (
        !error?.response ||
        status >= 500 ||
        (status === 422 &&
            [
                "ApplicantStageConflict",
                "ApplicantStageChangeIncomplete"
            ].includes(errorCode))
    );
};

export const submitApplicantStageChange = async (
    payload: Compleo.IObject,
    apiCall: ApiCall = defaultApiCall,
    generateRequestId: () => string = createRequestId
) => {
    const clientRequestId = generateRequestId();

    try {
        const response = await apiCall("/job/editapplicantjobstage", "post", {
            ...payload,
            clientRequestId
        });
        const acceptedResponse = {
            ...response,
            data: {
                ...response.data,
                warnings: response.data?.warnings || [],
                ...(response.status === 202 ? { pending: true } : {}),
                reconciled: false
            }
        };
        return acceptedResponse;
    } catch (error) {
        if (!shouldReconcile(error) || payload.oldStageId === payload.stageId) {
            throw error;
        }

        try {
            const reconciliation = await apiCall(
                "/job/getapplicantjobstage",
                "post",
                {
                    companyId: payload.companyId,
                    jobId: payload.jobId,
                    applicantId: payload.applicantId,
                    clientRequestId
                }
            );
            const operation = getOperationResponse(reconciliation);
            const requestMatches = operation.requestId === clientRequestId;

            if (
                requestMatches &&
                isTerminalOperation(operation.requestStatus)
            ) {
                return {
                    status: reconciliation.status,
                    data: {
                        ...operation,
                        reconciled: true
                    }
                };
            }
            if (
                requestMatches &&
                isRecoverableOperation(operation.requestStatus)
            ) {
                return {
                    status: 202,
                    data: {
                        ...operation,
                        requestId: clientRequestId,
                        requestStatus: operation.requestStatus || "retryable",
                        pending: true,
                        reconciled: true
                    }
                };
            }
        } catch {
            // The original mutation error is the useful error for the caller.
        }

        throw error;
    }
};

export const executeApplicantStageChange = async ({
    submit,
    rollback,
    onPersistenceError,
    onClose,
    synchronize,
    listKeyToInvalidate,
    invalidateQuery,
    onPersisted,
    onCompletionError,
    successResponse
}: ExecuteApplicantStageChangeParams) => {
    let response: ApplicantStageChangeResponse;
    try {
        response = await submit();
    } catch (error) {
        rollback();
        onPersistenceError(error);
        return (error as { response?: unknown })?.response;
    }

    if (![200, 202].includes(response.status)) {
        rollback();
        return response;
    }

    onClose();

    const reportCompletionError = (
        error: unknown,
        context: ApplicantStageCompletionErrorContext
    ) => {
        try {
            void Promise.resolve(onCompletionError?.(error, context)).catch(
                () => undefined
            );
        } catch {
            // Reporting must not change the outcome of a persisted stage change.
        }
    };

    try {
        void Promise.resolve(onPersisted(response)).catch((error) => {
            reportCompletionError(error, { operation: "notification" });
        });
    } catch (error) {
        reportCompletionError(error, { operation: "notification" });
    }

    try {
        if (response.data?.requestStatus !== "superseded") {
            try {
                await synchronize(response);
            } catch (error) {
                reportCompletionError(error, { operation: "synchronization" });
                // Persistence already succeeded; continue with cache invalidation.
            }
        }
    } finally {
        for (const key of listKeyToInvalidate) {
            try {
                void Promise.resolve(invalidateQuery(key)).catch((error) => {
                    reportCompletionError(error, {
                        operation: "invalidation",
                        queryKey: key
                    });
                });
            } catch (error) {
                reportCompletionError(error, {
                    operation: "invalidation",
                    queryKey: key
                });
                // Continue invalidating the remaining related queries.
            }
        }
    }

    return successResponse;
};

export const getApplicantStageChangeMessageKey = (response: any) => {
    const warnings = response?.data?.warnings || [];
    const isDirectAcceptedTransition =
        response?.status === 202 &&
        response?.data?.requestStatus === "accepted" &&
        response?.data?.reconciled === false;
    if (response?.data?.requestStatus === "manual_review") {
        return "COMPLEOGENERAL_MessageError";
    }
    if (response?.data?.pending && !isDirectAcceptedTransition) {
        return "ApplicantStageChangePendingWarning";
    }
    if (
        warnings.some((warning: any) =>
            emailAutomationWarningActions.has(warning?.action)
        )
    ) {
        return "ApplicantStageChangeAutomationWarning";
    }
    if (
        warnings.some(
            (warning: any) => warning?.action === "reconcileApplicantPriority"
        )
    ) {
        return "ApplicantStageChangePriorityWarning";
    }
    if (response?.data?.reconciled) {
        return "ApplicantStageChangeReconciledWarning";
    }
    return null;
};
